import sum from 'lodash/sum';
import uniqBy from 'lodash/uniqBy';
import { createSlice } from '@reduxjs/toolkit';
// utils
import { selectFrom, selectOne, insertInto, updateTable, callRPC } from '../../services/databaseService';
import { uploadFile, deleteFile } from '../../services/storageService';
import { invokeFunction } from '../../services/edgeFunctionService';
import { UserManager, UserState } from '../../@types/user';
import { collection, query, where, getDocs } from 'firebase/firestore';
//
import { dispatch } from '../store';
import { supabase } from 'src/services/supabase';

function normalizeVenueUser(row: any): UserManager {
  if (!row) return row;
  return {
    ...row,
    firstName: row.firstName || row.firstname || '',
    lastName: row.lastName || row.lastname || '',
    isVerified: row.isVerified ?? row.isverified ?? false,
    schedule_sync: row.schedule_sync ?? row.scheduleSync ?? false,
    access_profile_id: row.access_profile_id || row.access_profile?.id || null,
    role: row.role || row.access_profile?.code || '',
    venues: row.venues || [],
  };
}

// ----------------------------------------------------------------------

const initialState: UserState = {
  isLoading: false,
  error: null,
  users: [],
  user: null,
  sortBy: null,
  filters: {
    gender: [],
    category: 'All',
    rating: '',
  },
};

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET USERS
    getUsersSuccess(state, action) {
      state.isLoading = false;
      state.users = Array.isArray(action.payload) ? action.payload : action.payload.users || [];
    },

    // GET USER
    getUserSuccess(state, action) {
      state.isLoading = false;
      state.user = action.payload;
    },

    //  SORT & FILTER USERS
    sortByUsers(state, action) {
      state.sortBy = action.payload;
    },

    filterUsers(state, action) {
      state.filters.gender = action.payload.gender;
      state.filters.category = action.payload.category;
      state.filters.rating = action.payload.rating;
    },

    // STOP LOADING
    stopLoading(state) {
      state.isLoading = false;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  sortByUsers,
  filterUsers,
} = slice.actions;

// ----------------------------------------------------------------------

  // Helper function to get venue_user_id from current auth user
  async function getCurrentVenueUserId(): Promise<string | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: venueUser } = await supabase
        .from('venue_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      return venueUser?.id || null;
    } catch (error) {
      console.error('Error getting venue_user_id:', error);
      return null;
    }
  }

export function getUsers() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Get current venue_user_id
      const venueUserId = await getCurrentVenueUserId();
      if (!venueUserId) {
        dispatch(slice.actions.stopLoading());
        return;
      }

      // Query venue users
      // In Redux slice or service
      const users = await callRPC<any[]>('get_venue_users', {
        p_venue_user_id: venueUserId,
      });

      if (!Array.isArray(users)) {
        if ((users as any)?.error) {
          throw new Error((users as any).error);
        }
        dispatch(slice.actions.getUsersSuccess({ users: [] }));
        return;
      }

      dispatch(slice.actions.getUsersSuccess({
        users: users.map(normalizeVenueUser),
      }));
    } catch (error: any) {
      console.error('Error fetching users:', error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getUser(id: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const currentVenueUserId = await getCurrentVenueUserId();
      if (!currentVenueUserId) {
        dispatch(slice.actions.stopLoading());
        return;
      }

      let user: any;
      
      // If the requested id matches the authenticated user, use RPC function
      if (currentVenueUserId && id === currentVenueUserId) {
        const result = await callRPC<{
          user: {
            id: string;
            displayName: string;
            firstName: string;
            lastName: string;
            email: string;
            photo_url: string;
            role: string;
            status: string;
            isverified: boolean;
            schedule_sync: boolean;
            organization_id: string;
            organization_name?: string;
            plan_tier?: string;
            subscription?: {
              status?: string;
              billing_cycle?: string | null;
              current_period_end?: string | null;
              cancel_at_period_end?: boolean;
              pending_plan_tier?: string | null;
              pending_effective_at?: string | null;
              past_due_since?: string | null;
            } | null;
            auth_user_id: string;
            venues_ids: string[];
            created_at: string;
            updated_at: string;
          };
        }>('get_venue_user_profile');
        
        if (!result?.user) {
          throw new Error('User not found');
        }
        
        // Transform to match expected format
        user = normalizeVenueUser({
          id: result.user.id,
          firstname: result.user.firstName,
          lastname: result.user.lastName,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          email: result.user.email,
          photo_url: result.user.photo_url,
          role: result.user.role,
          status: result.user.status,
          isverified: result.user.isverified,
          schedule_sync: result.user.schedule_sync,
          organization_id: result.user.organization_id,
          plan_tier: result.user.plan_tier || 'starter',
          subscription: result.user.subscription ?? null,
          auth_user_id: result.user.auth_user_id,
          venues_ids: result.user.venues_ids,
          access_profile_id: (result.user as any).access_profile?.id,
          created_at: result.user.created_at,
          updated_at: result.user.updated_at,
        });
      } else {
        // For other users, use direct query
        user = await selectOne<any>('venue_users', { id });

      if (!user) {
        throw new Error('User not found');
        }
        
        // Get venue_ids for this user
        const { data: venueAuths } = await supabase
          .from('venue_auth')
          .select('venue_id')
          .eq('venue_user_id', user.id);
        
        user.venues_ids = venueAuths ? venueAuths.map((va: any) => va.venue_id) : [];
        user = normalizeVenueUser(user);
      }

      dispatch(slice.actions.getUserSuccess(user));
      return user;
    } catch (error: any) {
      console.error('Error fetching user:', error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function createUser(data: UserManager & { venue_ids?: string[] }) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Check if email already exists
      const existingUser = await selectOne<any>('venue_users', { email: data.email });
      if (existingUser?.organization_id) {
        throw new Error('Email address already being used.');
      }

      // Use Edge Function to create venue user (this will handle Supabase Auth creation)
      // The Edge Function should:
      // 1. Create Supabase Auth user
      // 2. Insert into venue_users with auth_user_id
      // 3. Generate organization_id, orderpassword, etc.
      const result = await invokeFunction<{
        user: any;
        auth_user_id: string;
      }>('create-venue-user', {
        email: data.email,
        firstname: data.firstName,
        lastname: data.lastName,
        password: null,
        role: data.role || null,
        access_profile_id: data.access_profile_id || null,
        schedule_sync: data.schedule_sync || false,
        venue_ids: (data.venue_ids || []).filter(Boolean),
        redirect_to: `${window.location.origin}/auth/invite/set-password`,
      });

      const userId = result.user.id;

      // Upload photo if provided
      if (data.avatarUrl && typeof data.avatarUrl !== 'string') {
        const file = data.avatarUrl as File;
        const photoPath = `venue-users/${userId}/photo_${Date.now()}.${file.name.split('.').pop()}`;
        await uploadFile('venue-user-photos', photoPath, file, {
          upsert: false,
        });

        // Update venue user with photo URL
        await updateTable('venue_users', {
          photo_url: photoPath,
        }, { id: userId });
      }

      // Fetch created user
      const user = normalizeVenueUser(await selectOne<any>('venue_users', { id: userId }));
      dispatch(slice.actions.getUserSuccess(user));
      return user;
    } catch (error: any) {
      console.error('Error creating user:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function resendVenueUserInvite(id: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await invokeFunction<{
        ok?: boolean;
        resent?: boolean;
        email?: string;
        error?: string;
        code?: string;
      }>('create-venue-user', {
        action: 'resend_invite',
        venue_user_id: id,
        redirect_to: `${window.location.origin}/auth/invite/set-password`,
      });

      if (result?.error) {
        const err = new Error(result.error);
        (err as any).code = result.code;
        throw err;
      }

      dispatch(slice.actions.stopLoading());
      return result;
    } catch (error: any) {
      console.error('Error resending invite:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function resetUserPassword(id: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Get venue user to find auth_user_id
      const user = await selectOne<any>('venue_users', { id });
      
      if (!user || !user.auth_user_id) {
        throw new Error('User not found or not linked to auth');
      }

      // Use Supabase Auth's native password recovery flow (no longer routed
      // through the custom send-email function — that path was only used by
      // the pre-Supabase Express backend and is no longer wired to a token).
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/auth/new-password`,
      });
      if (error) throw error;

      dispatch(slice.actions.stopLoading());
    } catch (error: any) {
      console.error('Error resetting user password:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateUser(data: UserManager) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const currentId = await getCurrentVenueUserId();
      const targetId = data.id || currentId;
      if (!targetId) {
        throw new Error('User not found');
      }

      const isSelf = targetId === currentId;
      const changingStaff = Boolean(data.access_profile_id) || Boolean(data.status);

      if (isSelf && !changingStaff) {
        await updateTable('venue_users', {
          firstname: data.firstName,
          lastname: data.lastName,
        }, { id: targetId });
      } else {
        const result = await callRPC<{ ok?: boolean; error?: string; code?: string }>(
          'update_venue_user_staff',
          {
            p_target_id: targetId,
            p_firstname: data.firstName || null,
            p_lastname: data.lastName || null,
            p_status: data.status || null,
            p_access_profile_id: data.access_profile_id || null,
            p_schedule_sync: data.schedule_sync ?? null,
          }
        );

        if (result?.error) {
          const err = new Error(result.error);
          (err as any).code = result.code;
          throw err;
        }
      }

      // Upload photo if provided
      if (data.avatarUrl && typeof data.avatarUrl === 'object') {
        // Delete old photo if exists
        if (data.photo_key) {
          await deleteFile('venue-user-photos', data.photo_key);
        }

        const photoPath = `venue-users/${targetId}/photo_${Date.now()}.${data.avatarUrl.name.split('.').pop()}`;
        await uploadFile('venue-user-photos', photoPath, data.avatarUrl, {
          upsert: false,
        });

        await updateTable('venue_users', {
          photo_url: photoPath,
        }, { id: targetId });
      }

      // Fetch updated user
      const user = normalizeVenueUser(await selectOne<any>('venue_users', { id: targetId }));
      dispatch(slice.actions.getUserSuccess(user));
      return user;
    } catch (error: any) {
      console.error('Error updating user:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateUserAuth(data: {
  venue_user_id: string;
  authorized_ids: string[];
  unauthorized_ids: string[];
  role?: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const venueUserId = data.venue_user_id;

      const authSet = new Set(data.authorized_ids || []);
      const unauthSet = new Set((data.unauthorized_ids || []).filter((id) => !authSet.has(id)));

      // Upsert for authorized_ids
      await Promise.all(
      Array.from(authSet).map(async (venueId) => {
        const existing = await selectOne<any>('venue_auth', {
        venue_user_id: venueUserId,
        venue_id: venueId,
        });

        if (existing) {
        const payload: any = {};
        if (data.role && data.role !== existing.role) payload.role = data.role;
        if (Object.keys(payload).length) {
          await updateTable('venue_auth', payload, {
          venue_user_id: venueUserId,
          venue_id: venueId,
          });
        }
        } else {
        const payload: any = { venue_user_id: venueUserId, venue_id: venueId };
        if (data.role) payload.role = data.role;
        await insertInto('venue_auth', payload);
        }
      })
      );

      // Delete for unauthorized_ids
      await Promise.all(
      Array.from(unauthSet).map(async (venueId) =>
        // assumes databaseService has deleteFrom; add import if missing
        (await import('../../services/databaseService')).deleteFrom('venue_auth', {
        venue_user_id: venueUserId,
        venue_id: venueId,
        })
      )
      );

      const user = await selectOne<any>('venue_users', { id: venueUserId });
      dispatch(slice.actions.getUserSuccess(user));
      return user;
    } catch (error: any) {
      console.error('Error updating user auth:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function removeUsersFromOrg(ids: string[]) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
      for (const id of uniqueIds) {
        const result = await callRPC<{ ok?: boolean; error?: string; code?: string }>(
          'remove_venue_user_from_org',
          { p_target_id: id }
        );
        if (result?.error) {
          const err = new Error(result.error);
          (err as any).code = result.code;
          throw err;
        }
      }
      dispatch(slice.actions.stopLoading());
    } catch (error: any) {
      console.error('Error removing users from org:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function verifyUser(id: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Get venue user to find auth_user_id
      const user = await selectOne<any>('venue_users', { id });
      
      if (!user || !user.auth_user_id) {
        throw new Error('User not found or not linked to auth');
      }

      // Use Supabase Auth's native resend flow (no longer routed through the
      // custom send-email function — that path was only used by the
      // pre-Supabase Express backend and is no longer wired to a token).
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
      });
      if (error) throw error;

      dispatch(slice.actions.stopLoading());
    } catch (error: any) {
      console.error('Error verifying user:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}